import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { trackEvent } from "@/lib/analytics";

const MEETING_URL = process.env.REACT_APP_HUBSPOT_MEETING_LINK_URL;

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden hero-radial noise"
    >
      {/* Grid */}
      <div className="absolute inset-0 grid-bg opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />

      {/* Animated eye/iris SVG accent */}
      <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[820px] h-[820px] hidden md:block pointer-events-none">
        <div className="relative w-full h-full">
          <div className="absolute inset-0 rounded-full bg-radial-glow opacity-80 animate-pulse-ring" />
          <svg viewBox="0 0 800 800" className="absolute inset-0 w-full h-full animate-floaty">
            <defs>
              <radialGradient id="iris" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#15B8B8" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#002D72" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#050a17" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="ring" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00C4C7" />
                <stop offset="100%" stopColor="#002D72" />
              </linearGradient>
            </defs>
            <circle cx="400" cy="400" r="280" fill="url(#iris)" />
            {Array.from({ length: 48 }).map((_, i) => {
              const angle = (i / 48) * Math.PI * 2;
              const x1 = 400 + Math.cos(angle) * 150;
              const y1 = 400 + Math.sin(angle) * 150;
              const x2 = 400 + Math.cos(angle) * 280;
              const y2 = 400 + Math.sin(angle) * 280;
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="url(#ring)"
                  strokeWidth="1.4"
                  opacity={0.5 + (i % 3) * 0.15}
                />
              );
            })}
            <circle cx="400" cy="400" r="130" fill="#050a17" />
            <circle cx="400" cy="400" r="60" fill="#000" />
            <circle cx="400" cy="400" r="280" fill="none" stroke="url(#ring)" strokeWidth="1" opacity="0.6" />
            <circle cx="400" cy="400" r="340" fill="none" stroke="#15B8B8" strokeWidth="0.5" opacity="0.35" strokeDasharray="3 8" />
            <circle cx="400" cy="400" r="380" fill="none" stroke="#15B8B8" strokeWidth="0.5" opacity="0.2" strokeDasharray="2 14" />
          </svg>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 w-full">
        <ScrollReveal>
          <div className="inline-flex items-center gap-3 rounded-full px-4 py-2 bg-white/5 border border-teal/25 backdrop-blur">
            <span className="h-2 w-2 rounded-full bg-teal2 animate-pulse" />
            <span className="text-xs tracking-[0.2em] uppercase text-teal2 font-semibold">
              Regenerative Ophthalmology
            </span>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={120}>
          <h1 className="mt-8 font-display text-white text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] max-w-3xl">
            Transforming{" "}
            <span className="text-gradient-teal-bright">ocular surface</span>{" "}
            care.
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={220}>
          <p className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed">
            Advanced regenerative solutions designed to improve patient outcomes,
            increase practice efficiency, and elevate the standard of care.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={320}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            {MEETING_URL ? (
              <a
                href={MEETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("request_demo_click", { location: "hero" })}
                data-testid="hero-cta-consult"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-teal text-white px-8 py-4 text-sm font-semibold hover:bg-teal2 transition-all shadow-[0_18px_40px_-12px_rgba(21,184,184,0.7)]"
              >
                Schedule a Consultation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            ) : (
              <Link
                to="/contact"
                onClick={() => trackEvent("request_demo_click", { location: "hero" })}
                data-testid="hero-cta-consult"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-teal text-white px-8 py-4 text-sm font-semibold hover:bg-teal2 transition-all shadow-[0_18px_40px_-12px_rgba(21,184,184,0.7)]"
              >
                Schedule a Consultation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            <Link
              to="/xcellereyes"
              data-testid="hero-cta-learn"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white px-8 py-4 text-sm font-semibold hover:border-teal hover:text-teal transition-all backdrop-blur"
            >
              <Play className="h-4 w-4" />
              Learn About XcellerEYES
            </Link>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={460}>
          <div className="mt-16 md:mt-20 grid grid-cols-3 gap-3 sm:gap-6 md:gap-12 max-w-2xl">
            {[
              { v: "24mo", l: "Ambient Storage" },
              { v: "Single-use", l: "Sterile Allograft" },
              { v: "70+", l: "Ocular Indications" },
            ].map((s) => (
              <div key={s.l} className="border-l border-teal/30 pl-3 sm:pl-4 min-w-0">
                <div className="font-display text-lg sm:text-2xl md:text-3xl text-white font-medium leading-tight break-words">{s.v}</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] text-white/55 mt-1.5 sm:mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>

      {/* Bottom gradient fade to white section */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none" />
    </section>
  );
}
